import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/AuthContext'
import { executeRecaptcha } from '@/lib/utils/recaptcha'
import { createAgentsCheckout } from '@/lib/api/billing'
import {
  SystemCardShell,
  SystemCardHeading,
  SystemCardSecondary,
  SystemCardProgress,
  SystemCardFooter,
} from '@/components/shared/layout'

const API_URL = import.meta.env.VITE_PUBLIC_API_URL || 'http://localhost:3000'
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY || ''

type Step = 'verifying' | 'ready' | 'error'

const errorCode = (err: unknown) =>
  err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : ''

/**
 * Agent-led sign-up (backend `GET /agent.md`). The agent opens a session, the
 * person lands here, we confirm they are human and create the account on Free
 * start: the first 25,000 data points free, no card, API and MCP on. The agent's
 * poll completes as soon as the account exists. The Agents plan ($39 one time,
 * 100,000 data points) is offered as the optional paid step, never required.
 */
export default function AgentVerify() {
  const [searchParams] = useSearchParams()
  const { setToken } = useAuth()
  const token = searchParams.get('token') || ''

  const [step, setStep] = useState<Step>('verifying')
  const [errorMsg, setErrorMsg] = useState('')
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [checkoutPending, setCheckoutPending] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')

  // Run reCAPTCHA invisibly on mount and create the account
  useEffect(() => {
    if (!token) {
      setErrorMsg('This link is missing its session. Ask your agent to start over.')
      setStep('error')
      return
    }

    const run = async () => {
      let captchaToken = ''
      try {
        captchaToken = await executeRecaptcha(RECAPTCHA_SITE_KEY, 'agent_signup')
      } catch {
        // Non-blocking — backend allows empty tokens in dev
      }
      await handleVerify(captchaToken)
    }

    run()
  }, [token])

  const handleVerify = async (captchaToken: string) => {
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/agent-captcha-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, captchaToken }),
        credentials: 'include',
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (data.error === 'session_expired') {
          setErrorMsg('This link has expired. Ask your agent to start over.')
        } else {
          setErrorMsg('Something went wrong on our side. Close this tab and ask your agent to try again.')
        }
        setStep('error')
        return
      }

      const data = await res.json()
      setToken(data.authToken)
      setAuthToken(data.authToken)
      setStep('ready')
    } catch {
      setErrorMsg('Something went wrong on our side. Close this tab and ask your agent to try again.')
      setStep('error')
    }
  }

  const handleAgentsPlan = async () => {
    if (!authToken) return
    setCheckoutPending(true)
    setCheckoutError('')
    try {
      const { url } = await createAgentsCheckout(authToken, {
        successPath: '/agent-complete?session_id={CHECKOUT_SESSION_ID}',
        cancelPath: `/agent-verify?token=${encodeURIComponent(token)}&checkout=canceled`,
      })
      window.location.href = url
    } catch (err) {
      const code = errorCode(err)
      setCheckoutError(
        code === 'already_subscribed'
          ? 'This account already has a plan. You can manage it in Account settings.'
          : code === 'checkout_unavailable'
            ? "The Agents plan isn't switched on yet. Your free data points work in the meantime."
            : "Checkout didn't open. Try again in a moment.",
      )
      setCheckoutPending(false)
    }
  }

  return (
    <SystemCardShell>
      {step === 'verifying' && (
        <>
          <SystemCardHeading eyebrow="Almost there" title="Setting up your account">
            Linking this browser to the account your agent started for you.
          </SystemCardHeading>
          <SystemCardProgress label="Verifying" />
        </>
      )}

      {step === 'ready' && (
        <>
          <SystemCardHeading success eyebrow="You're in" title="Head back to your agent">
            Your first 25,000 data points are free. No card needed. Your agent can add your
            website and start answering questions now.
          </SystemCardHeading>

          <div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
            <div className="flex flex-col gap-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-medium leading-5 text-foreground">Agents plan</span>
                <span className="text-[13px] font-medium leading-5 tabular-nums text-foreground">$39 one time</span>
              </span>
              <span className="text-xs leading-[18px] text-muted-foreground">
                100,000 data points for 12 months. Not a subscription. Top up later at $29 per 100,000.
              </span>
            </div>
            <SystemCardSecondary onClick={handleAgentsPlan} disabled={checkoutPending}>
              {checkoutPending ? 'Opening checkout' : 'Get the Agents plan'}
            </SystemCardSecondary>
            {checkoutError && (
              <p role="alert" className="text-xs leading-[18px] text-muted-foreground">
                {checkoutError}
              </p>
            )}
          </div>

          <SystemCardFooter>
            Optional. Buy it now or when your free data points run low. Want the full dashboard?
            Business and Scale are in Account settings.
          </SystemCardFooter>
        </>
      )}

      {step === 'error' && (
        <SystemCardHeading eyebrow="Almost there" title="This link didn't work">
          {errorMsg}
        </SystemCardHeading>
      )}
    </SystemCardShell>
  )
}
